/**
 * ChangelogPage - Página con el historial de actualizaciones
 */

import React from 'react';
import { Layout } from '@/components/Layout';
import {
  Rocket,
  Wifi,
  Server,
  Send,
  Download,
  Sparkles,
  Calendar,
  CheckCircle,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import {
  RELEASE_NOTES,
  formatReleaseDate,
  isV2Released,
  getTimeUntilRelease,
} from '@/lib/featureFlags';

// Map icon names to components
const iconMap = {
  Wifi: Wifi,
  Server: Server,
  Send: Send,
  Download: Download,
  Sparkles: Sparkles,
};

export default function ChangelogPage() {
  const navigate = useNavigate();
  const timeLeft = getTimeUntilRelease();
  const isReleased = isV2Released();
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#34C759] to-[#007AFF] flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#F4F4F5]">Novedades y Actualizaciones</h1>
              <p className="text-[#71717A]">Historial de versiones de AVX RF PRO</p>
            </div>
          </div>
        </div>
        
        {/* Timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-[#34C759] via-[#007AFF] to-[#52525B]" />
          
          {/* Releases */}
          <div className="space-y-8">
            {RELEASE_NOTES.map((release, releaseIndex) => {
              const releaseDate = new Date(release.date);
              const isPast = new Date() >= releaseDate;
              
              return (
                <div key={release.version} className="relative pl-16">
                  {/* Timeline dot */}
                  <div className={`absolute left-4 w-5 h-5 rounded-full border-4 ${
                    isPast 
                      ? 'bg-[#34C759] border-[#34C759]/30' 
                      : 'bg-[#007AFF] border-[#007AFF]/30 animate-pulse'
                  }`} />
                  
                  {/* Release Card */}
                  <div className={`rounded-xl border overflow-hidden ${
                    releaseIndex === 0 
                      ? 'bg-gradient-to-b from-[#111111] to-[#0A0A0A] border-[#34C759]/30' 
                      : 'bg-[#111111] border-white/10'
                  }`}>
                    {/* Release Header */}
                    <div className="p-6 border-b border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <h2 className="text-xl font-bold text-white">v{release.version}</h2>
                          {releaseIndex === 0 && (
                            <Badge className={isPast 
                              ? 'bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30' 
                              : 'bg-[#007AFF]/20 text-[#007AFF] border-[#007AFF]/30 animate-pulse'
                            }>
                              {isPast ? (
                                <><CheckCircle className="w-3 h-3 mr-1" /> Disponible</>
                              ) : (
                                <><Clock className="w-3 h-3 mr-1" /> Próximamente</>
                              )}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#71717A]">
                          <Calendar className="w-4 h-4" />
                          {releaseDate.toLocaleDateString('es-CL', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </div>
                      </div>
                      <h3 className="text-lg text-[#A1A1AA]">{release.title}</h3>
                      
                      {/* Countdown for unreleased */}
                      {!isPast && releaseIndex === 0 && (
                        <div className="mt-4 p-4 rounded-lg bg-[#007AFF]/10 border border-[#007AFF]/30">
                          <p className="text-sm text-[#007AFF] mb-2">Tiempo restante:</p>
                          <div className="flex gap-4">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-white">{timeLeft.days}</p>
                              <p className="text-xs text-[#71717A]">días</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-white">{timeLeft.hours}</p>
                              <p className="text-xs text-[#71717A]">horas</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-white">{timeLeft.minutes}</p>
                              <p className="text-xs text-[#71717A]">min</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Features */}
                    <div className="p-6">
                      <div className="grid gap-4 md:grid-cols-2">
                        {release.highlights.map((feature, index) => {
                          const IconComponent = iconMap[feature.icon] || Sparkles;
                          return (
                            <div
                              key={index}
                              className={`p-4 rounded-lg border transition-colors ${
                                isPast 
                                  ? 'bg-white/[0.02] border-white/[0.05] hover:bg-white/[0.05]' 
                                  : 'bg-white/[0.01] border-white/[0.03] opacity-75'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div
                                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                                  style={{ backgroundColor: `${feature.color}15` }}
                                >
                                  <IconComponent
                                    className="w-5 h-5"
                                    style={{ color: feature.color }}
                                  />
                                </div>
                                <div>
                                  <h4 className="font-medium text-white mb-1">{feature.title}</h4>
                                  <p className="text-sm text-[#71717A]">{feature.description}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* CTA for released features */}
                      {isPast && releaseIndex === 0 && (
                        <div className="mt-6 flex gap-3">
                          <Button
                            onClick={() => navigate('/hardware')}
                            className="bg-gradient-to-r from-[#34C759] to-[#007AFF] hover:opacity-90 text-white"
                          >
                            Explorar Control RF en Red
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => navigate('/profile')}
                          >
                            Configurar Alertas Telegram
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Older versions placeholder */}
            <div className="relative pl-16">
              <div className="absolute left-4 w-5 h-5 rounded-full bg-[#52525B] border-4 border-[#52525B]/30" />
              <div className="p-6 rounded-xl bg-[#111111] border border-white/10">
                <h2 className="text-lg font-semibold text-white mb-2">v1.x - Versiones Anteriores</h2>
                <ul className="text-sm text-[#71717A] space-y-1">
                  <li>• Escaneo de espectro con Relacart DISCOVER</li>
                  <li>• Gestión de sistemas y frecuencias</li>
                  <li>• Cálculo de intermodulación</li>
                  <li>• Importación desde Wireless Workbench</li>
                  <li>• Mapas de cobertura RF</li>
                  <li>• Racks y asignación de frecuencias</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
